import { inject, Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivateChild, Router, RouterStateSnapshot } from "@angular/router";
import { MsalService } from "@azure/msal-angular";
import { AccountInfo } from "@azure/msal-browser";
import { firstValueFrom } from "rxjs";

import { AzureAdService } from "@services/azure-ad.service";
import { LocalStorageService } from "@services/local-storage.service";
import { SessionStorageService } from "@services/session-storage.service";
import { environment } from "environments/environments";
import { Auditor } from "./Domain/Models/auditor.model";

@Injectable({ providedIn: "root" })
export class CanActivateGuard implements CanActivateChild {
    auditor: Auditor;

    private msalService = inject(MsalService);
    private router = inject(Router);
    private sessionStorage = inject(SessionStorageService);
    private azureAdService = inject(AzureAdService);
    private localStorageService = inject(LocalStorageService);

    async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        try {
            // MSAL v3+ exige initialize() antes de QUALQUER API (getAllAccounts, acquireTokenSilent...).
            // O guard reexecuta a cada navegação (canActivateChild); no ramo com contas ele não passava
            // pelo trySilentSignIn (único ponto que inicializava), então acquireTokenSilent falhava com
            // "uninitialized_public_client_application" e o checkUser pendurava, travando a navegação.
            // initialize() é idempotente.
            await this.msalService.instance.initialize();

            let accounts = this.msalService.instance.getAllAccounts();

            // Aba nova/reload: com o cache do MSAL em sessionStorage (isolado por aba), não há
            // contas nesta aba. Antes de mandar para o login, tenta restaurar a sessão de forma
            // silenciosa via cookie de SSO do Azure AD (ssoSilent). Só cai no login se falhar.
            if (accounts.length === 0) {
                const restored = await this.trySilentSignIn();
                if (!restored) {
                    this.redirectToLogin(state);
                    return false;
                }
                accounts = this.msalService.instance.getAllAccounts();
            }

            if (accounts.length > 0) {
                // Só considera verificado se TAMBÉM há o auditor no storage desta aba. Numa aba
                // nova (sessionStorage isolado) ambos vêm vazios; evita liberar a rota com o
                // auditor undefined (componentes leem sessionStorage.get("auditor") no construtor).
                if (this.sessionStorage.get("isUserVerified") && this.sessionStorage.get("auditor")) {
                    return true;
                }

                // Adquire um token válido silenciosamente antes das chamadas HTTP (evita redirect
                // do MsalInterceptor durante a resolução do guard). Se falhar, NÃO libera a rota
                // cegamente — segue para o checkUser (o interceptor renova o token na chamada);
                // liberar sem o auditor deixaria os componentes com auditor undefined.
                try {
                    await this.msalService.instance.acquireTokenSilent({
                        scopes: environment.scopeUrl,
                        account: accounts[0]
                    });
                } catch {
                    // Segue para o checkUser mesmo assim; a renovação ocorre no interceptor.
                }

                const culture = window.navigator.languages ? window.navigator.languages[0] : null;
                const observable = await this.azureAdService.checkUser(culture);
                this.auditor = await firstValueFrom(observable, {
                    defaultValue: null
                }).catch((err) => {
                    return null;
                });

                if (this.auditor != null) {
                    this.sessionStorage.set("auditor", this.auditor);
                    this.sessionStorage.set("isUserVerified", true);
                    this.localStorageService.set("lang", this.auditor.idiomaAuditorInterface);
                    return true;
                } else {
                    this.sessionStorage.remove("auditor");
                    this.sessionStorage.set("isUserVerified", false);
                    this.router.navigate(["/not-authorized"]);
                }
                return false;
            } else {
                this.redirectToLogin(state);
            }
            return false;
        } catch (error) {
            this.redirectToLogin(state);
            return false;
        }
    }

    /**
     * Redireciona para /login preservando a rota originalmente solicitada como returnUrl,
     * para que o LoginComponent volte a ela após a autenticação. Não preserva returnUrl
     * quando a URL de origem já é o próprio /login (evita loop/redundância).
     */
    private redirectToLogin(state: RouterStateSnapshot): void {
        const target = state?.url;
        const isLoginRoute = !target || target === "/" || target.startsWith("/login");

        this.router.navigate(["/login"], {
            queryParams: isLoginRoute ? {} : { returnUrl: target }
        });
    }

    /**
     * Tenta readquirir a sessão silenciosamente (ssoSilent) usando o cookie de SSO do Azure AD,
     * quando esta aba não tem conta em cache (sessionStorage isolado por aba). Em sucesso, define
     * a conta ativa e retorna true. Em falha (sem sessão de SSO recuperável ou interação exigida),
     * retorna false para que o chamador redirecione ao login.
     */
    private async trySilentSignIn(): Promise<boolean> {
        try {
            // O MSAL v3+ exige initialize() antes de qualquer API. O guard pode rodar antes de o
            // MSAL Angular concluir a inicialização; garante-a aqui (idempotente) para evitar
            // "uninitialized_public_client_application".
            await this.msalService.instance.initialize();

            const result = await this.msalService.instance.ssoSilent({
                scopes: environment.scopeUrl,
                // Página de redirect dedicada e mínima (sem bootstrap do Angular) para o fluxo
                // por iframe. Evita que o iframe recarregue a SPA inteira ao voltar do authorize,
                // que fazia o ssoSilent expirar. Precisa estar registrada como redirect URI (SPA)
                // no Azure AD. Ver public/assets/msal-redirect.html e ADR-004.
                redirectUri: `${window.location.origin}/assets/msal-redirect.html`
            });

            const account: AccountInfo | null = result.account ?? null;
            if (!account) return false;

            this.msalService.instance.setActiveAccount(account);
            return true;
        } catch {
            return false;
        }
    }

    canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        return this.canActivate(route, state);
    }
}
